<template>
  <div class="admin-panel task-page task-page--incorporation">

    <admin-header
      variant="back"
      :crumbs="[{ label: 'Client Management', to: { name: 'Admin-Panel', params: { lang: $route.params.lang } } }, { label: 'Incorporation Tasks' }]"
      :back-to="{ name: 'Admin-Panel', params: { lang: $route.params.lang } }"
      @open-logout="openLogoutModal"
    />

    <main class="ap-container ap-fade-in" v-cloak>

      <div class="tp-hero">
        <div class="tp-hero-icon">
          <fa-icon :icon="['fas', 'gavel']" />
        </div>
        <div>
          <h1 class="tp-hero-title">Incorporation</h1>
          <p class="tp-hero-sub">Track and manage company incorporation and registration tasks.</p>
        </div>
      </div>

      <div class="tp-summary">
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ clients.length }}</span>
          <span class="tp-summary-label">Clients</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ pendingCount }}</span>
          <span class="tp-summary-label">Pending Tasks</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ reviewCount }}</span>
          <span class="tp-summary-label">In Review</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ allDoneCount }}</span>
          <span class="tp-summary-label">All Done</span>
        </div>
      </div>

      <div class="tp-panel" v-cloak>
        <div class="tp-panel-head">
          <h3>Incorporation Task List</h3>
          <div class="tp-panel-head-right">
            <span v-if="!loading" class="tp-panel-meta">{{ clients.length }} client{{ clients.length !== 1 ? 's' : '' }}</span>
          </div>
        </div>

        <div v-if="loading" class="tp-empty">
          <fa-icon :icon="['fas', 'spinner']" spin class="tp-empty-icon" />
          <p>Loading incorporation clients…</p>
        </div>

        <div v-else-if="!clients.length" class="tp-empty">
          <fa-icon :icon="['fas', 'gavel']" class="tp-empty-icon" />
          <p>No incorporation clients found. Add clients with the Incorporation service to see their tasks here.</p>
        </div>

        <div ref="calWrap" class="tp-cal-wrap" :style="{ height: tableHeight }">
          <table class="tp-cal-table">
            <colgroup>
              <col class="tp-col-num">
              <col class="tp-col-company">
              <col class="tp-col-initial">
              <col class="tp-col-meta">
              <col class="tp-col-meta">
              <col class="tp-col-meta">
              <col class="tp-col-meta">
              <col class="tp-col-meta">
              <col class="tp-col-meta">
              <col class="tp-col-meta">
              <col class="tp-col-meta">
              <col class="tp-col-meta">
              <col v-for="t in DATE_TASKS" :key="'col-dt-' + t.name" class="tp-col-inc-date">
              <col v-for="t in STATUS_TASKS" :key="'col-st-' + t.name" class="tp-col-inc-status">
              <col class="tp-col-actions">
            </colgroup>
            <thead>
              <tr>
                <th class="tp-cal-th tp-cal-sticky tp-cal-th-num" style="left:0">#</th>
                <th class="tp-cal-th tp-cal-sticky tp-cal-th-company" style="left:50px">Client</th>
                <th class="tp-cal-th">Initial</th>
                <th class="tp-cal-th">SSM (New)</th>
                <th class="tp-cal-th">SSM (Old)</th>
                <th class="tp-cal-th">Tax Group</th>
                <th class="tp-cal-th">Tax No.</th>
                <th class="tp-cal-th">E Prefix</th>
                <th class="tp-cal-th">E No.</th>
                <th class="tp-cal-th">Auditor</th>
                <th class="tp-cal-th">Year End</th>
                <th class="tp-cal-th">Inc. Date</th>
                <th v-for="t in DATE_TASKS" :key="'th-dt-' + t.name" class="tp-cal-th tp-inc-th-dated">{{ t.label }}</th>
                <th v-for="t in STATUS_TASKS" :key="'th-st-' + t.name" class="tp-cal-th tp-inc-th-status">{{ t.label }}</th>
                <th class="tp-cal-th"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(client, i) in clients" :key="client.id" class="tp-cal-row">
                <td class="tp-cal-td tp-cal-sticky tp-cal-td-num" style="left:0">{{ i + 1 }}</td>
                <td class="tp-cal-td tp-cal-sticky tp-cal-td-company" style="left:50px">{{ client.client }}</td>
                <td class="tp-cal-td tp-cal-td-meta">{{ client.initial || '—' }}</td>
                <td class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !client.ssm_number_new }">{{ client.ssm_number_new || '—' }}</td>
                <td class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !client.ssm_number_old }">{{ client.ssm_number_old || '—' }}</td>
                <td class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !(client.tax_number && client.tax_number.group) }">{{ (client.tax_number && client.tax_number.group) || '—' }}</td>
                <td class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !(client.tax_number && client.tax_number.number) }">{{ (client.tax_number && client.tax_number.number) || '—' }}</td>
                <td class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !(client.e_number && client.e_number.prefixed) }">{{ (client.e_number && client.e_number.prefixed) || '—' }}</td>
                <td class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !(client.e_number && client.e_number.number) }">{{ (client.e_number && client.e_number.number) || '—' }}</td>
                <td class="tp-cal-td tp-cal-td-meta" :class="{ 'ap-cell-empty': !client.auditor }">{{ client.auditor || '—' }}</td>
                <td class="tp-cal-td tp-cal-td-meta" :class="{ 'ap-cell-empty': !client.year_end }">{{ formatYearEnd(client.year_end) }}</td>
                <td class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !client.incorporation_date }">{{ formatIncDate(client.incorporation_date) }}</td>
                <td
                  v-for="t in DATE_TASKS"
                  :key="'td-dt-' + client.id + '-' + t.name"
                  class="tp-cal-td tp-inc-td-dated"
                >{{ formatTaskDate(client.tasks[t.name]) }}</td>
                <td
                  v-for="t in STATUS_TASKS"
                  :key="'td-st-' + client.id + '-' + t.name"
                  class="tp-cal-td tp-inc-td-status"
                >
                  <div class="tp-inc-status-wrap" :class="'tp-inc-s--' + getTaskStatus(client, t.name)">
                    <fa-icon
                      v-if="updatingTask[client.id + '-' + t.name]"
                      :icon="['fas', 'spinner']"
                      spin
                      class="tp-inc-spinner"
                    />
                    <select
                      v-else
                      class="tp-inc-status-sel"
                      :value="getTaskStatus(client, t.name)"
                      @change="updateStatus(client, t.name, $event.target.value)"
                    >
                      <option value="pending">Pending</option>
                      <option value="review">Review</option>
                    </select>
                  </div>
                </td>
                <td class="tp-cal-td tp-cal-td-actions">
                  <div class="ap-row-actions">
                    <button class="ap-row-action" title="View" @click="viewingClient = client; showViewModal = true">
                      <fa-icon :icon="['fas', 'eye']" />
                    </button>
                    <button class="ap-row-action" title="Edit" @click="$router.push({ name: 'Admin-Edit-Client', params: { lang: $route.params.lang, id: client.id } })">
                      <fa-icon :icon="['fas', 'edit']" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </main>

    <client-details-modal
      :show="showViewModal"
      :client="viewingClient"
      :show-documents="false"
      @close="showViewModal = false"
    />

    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="closeLogoutModal"
      @confirm="confirmLogout"
    >Are you sure you want to logout?</confirm-modal>

    <session-modal :show="showAdminSessionModal" @continue="continueAdminSession" @signout="signOutAdminSession">
      Your admin session has expired. Continue to refresh your session or sign out.
    </session-modal>

  </div>
</template>

<script>
import adminSessionMixin from '@/mixins/adminSessionMixin'
import AdminHeader from '@/components/admin/AdminHeader.vue'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import SessionModal from '@/components/shared/SessionModal.vue'
import ClientDetailsModal from '@/components/shared/ClientDetailsModal.vue'

const DATE_TASKS = [
  { name: 'submit_S58_236', label: 'Submit S58 & 236(2)' },
  { name: 'submit_S472_473', label: 'Submit S47(2) / S47(3)' },
  { name: 'submit_beneficial_ownership', label: 'Submit Beneficial Ownership' },
  { name: 'pay_form_m1_stamp_duty', label: 'Pay M1 Stamp Duty' },
  { name: 'pay_form_m3_stamp_duty', label: 'Pay M3 Stamp Duty' }
]

const STATUS_TASKS = [
  { name: 'order_company_stamp', label: 'Order Company Stamp' },
  { name: 'create_superior_file', label: 'Create Superior File' },
  { name: 'create_physical_file', label: 'Create Physical File' },
  { name: 'submit_form_1st_cp204', label: 'Submit 1st CP204' }
]

export default {
  name: 'admin-task-incorporation',
  mixins: [adminSessionMixin],
  components: { AdminHeader, ConfirmModal, SessionModal, ClientDetailsModal },
  data () {
    return {
      DATE_TASKS,
      STATUS_TASKS,
      clients: [],
      loading: false,
      updatingTask: {},
      monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      tableHeight: 'auto',
      viewingClient: null,
      showViewModal: false
    }
  },
  computed: {
    pendingCount () {
      let n = 0
      this.clients.forEach(c => {
        STATUS_TASKS.forEach(t => { if (this.getTaskStatus(c, t.name) === 'pending') n++ })
      })
      return n
    },
    reviewCount () {
      let n = 0
      this.clients.forEach(c => {
        STATUS_TASKS.forEach(t => { if (this.getTaskStatus(c, t.name) === 'review') n++ })
      })
      return n
    },
    allDoneCount () {
      return this.clients.filter(c => STATUS_TASKS.every(t => this.getTaskStatus(c, t.name) === 'review')).length
    }
  },
  mounted () {
    this.startSessionTimer()
    this.fetchClients()
    window.addEventListener('resize', this.computeTableHeight)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.computeTableHeight)
  },
  methods: {
    computeTableHeight () {
      if (!this.$refs.calWrap) return
      this.tableHeight = 'auto'
      this.$nextTick(() => {
        const wrap = this.$refs.calWrap
        if (!wrap) return
        const h = wrap.scrollHeight
        this.tableHeight = `${h < 600 ? h + 15 : 600}px`
      })
    },
    async fetchClients () {
      this.loading = true
      try {
        const data = await this.adminFetch('/api/admin/tasks/incorporation-clients.php')
        if (data === null) { this.pendingAdminRetry = () => this.fetchClients(); return }
        if (data && data.success) {
          this.clients = data.data || []
          this.$nextTick(this.computeTableHeight)
        }
      } catch (err) {
        console.error('Failed to load incorporation clients:', err)
      } finally {
        this.loading = false
      }
    },
    formatYearEnd (month) {
      if (!month) return '—'
      return this.monthNames[month - 1] || '—'
    },
    formatIncDate (dateStr) {
      if (!dateStr) return '—'
      const parts = dateStr.split('-')
      if (parts.length !== 3) return dateStr
      return `${parseInt(parts[2])}-${parseInt(parts[1])}-${parts[0]}`
    },
    formatTaskDate (task) {
      if (!task || !task.date) return '—'
      const { day, month } = task.date
      if (!day || !month) return '—'
      return `${day}-${this.monthNames[month - 1] || month}`
    },
    getTaskStatus (client, taskName) {
      return (client.tasks && client.tasks[taskName] && client.tasks[taskName].status) || 'pending'
    },
    async updateStatus (client, taskName, newStatus) {
      const key = `${client.id}-${taskName}`
      this.$set(this.updatingTask, key, true)
      try {
        const data = await this.adminFetch('/api/admin/tasks/update-incorporation-status.php', {
          method: 'POST',
          body: JSON.stringify({ client_id: client.id, task_name: taskName, status: newStatus })
        })
        if (data === null) { this.pendingAdminRetry = () => this.updateStatus(client, taskName, newStatus); return }
        if (data && data.success) {
          if (!client.tasks) this.$set(client, 'tasks', {})
          if (!client.tasks[taskName]) this.$set(client.tasks, taskName, {})
          this.$set(client.tasks[taskName], 'status', newStatus)
        }
      } catch (err) {
        console.error('Failed to update incorporation task status:', err)
      } finally {
        this.$set(this.updatingTask, key, false)
      }
    }
  }
}
</script>
